<script setup lang="ts">
import { LucideUser } from 'lucide-vue-next'
import type { ICommentWithAuthor, ICommentFormData } from './types'
import { COMMENTS_MAX_DEPTH } from './types'
import WidgetCommentForm from './WidgetCommentForm.vue'
import { useUser } from '~/composables/useUser'
import { useAuthModalStore } from '~/stores/authModal'

const props = defineProps<{
  comment: ICommentWithAuthor
  depth?: number
  activeReplyId?: number | null
  onSubmit?: (data: ICommentFormData) => Promise<boolean>
  onSetActiveReply?: (commentId: number | null) => void
}>()

const { isLoggedIn } = useUser()
const authModalStore = useAuthModalStore()

const maxDepth = COMMENTS_MAX_DEPTH

const isReplying = computed(() => props.activeReplyId === props.comment.id)

const formattedDate = computed(() => {
  if (!props.comment.updated_at) return ''
  return parseSiteDate(props.comment.updated_at).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: SITE_TIME_ZONE
  })
})

const handleReplyClick = () => {
  if (!isLoggedIn.value) {
    authModalStore.openLoginModal()
    return
  }
  props.onSetActiveReply?.(props.comment.id)
}

const handleCancelReply = () => {
  props.onSetActiveReply?.(null)
}

const handleSubmitReply = async (data: ICommentFormData): Promise<boolean> => {
  if (props.onSubmit) {
    const parentId =
      props.depth !== undefined && props.depth >= maxDepth
        ? props.comment.parent_id
        : props.comment.id

    return props.onSubmit({ ...data, parent_id: parentId })
  }
  return false
}
</script>

<template>
  <div>
    <div class="flex gap-3 py-4">
      <!-- Avatar -->
      <div class="shrink-0">
        <div
          v-if="!comment.user?.avatar"
          class="flex size-6 items-center justify-center rounded-lg bg-gray-100 text-gray-600 md:size-9"
        >
          <LucideUser class="size-3 md:size-5" />
        </div>
        <NuxtImg
          v-else
          :src="comment.user.avatar"
          :alt="comment.user?.fullname || 'User'"
          class="size-6 rounded-lg object-cover md:size-9"
        />
      </div>

      <!-- Comment content -->
      <div class="min-w-0 flex-1">
        <!-- Author and date -->
        <div class="mb-1 flex flex-wrap items-center gap-2 text-sm">
          <span class="font-medium text-gray-900">
            {{ comment.user?.fullname || $t('widget.comment.item.anonymous') }}
          </span>
          <span class="text-gray-400">•</span>
          <time class="text-sm text-gray-500">{{ formattedDate }}</time>
          <Badge
            v-if="comment.is_moderation_required"
            rounded="full"
            class="bg-blue-50 font-normal text-gray-900"
          >
            {{ $t('widget.comment.item.moderation_required') }}
          </Badge>
        </div>

        <!-- Comment text -->
        <div class="mb-2 leading-relaxed text-gray-700" v-html="comment.text" />

        <!-- Reply button -->
        <button
          type="button"
          class="font-medium text-gray-500 hover:text-gray-700"
          @click="handleReplyClick"
        >
          {{ $t('widget.comment.form.reply_button') }}
        </button>

        <!-- Inline Reply Form -->
        <div v-if="isReplying" class="mt-3">
          <WidgetCommentForm
            :parent-id="comment.id"
            :parent-author="comment.user?.fullname"
            :on-cancel="handleCancelReply"
            @submit="handleSubmitReply"
          />
        </div>

        <!-- Nested replies -->
        <div v-if="comment.replies && comment.replies.length > 0" class="mt-3">
          <WidgetCommentItem
            v-for="reply in comment.replies"
            :key="reply.id"
            :comment="reply"
            :depth="(depth || 0) + 1"
            :active-reply-id="activeReplyId"
            :on-submit="onSubmit"
            :on-set-active-reply="onSetActiveReply"
          />
        </div>
      </div>
    </div>
  </div>
</template>
